import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { DocumentsService, CommonService } from '../../services/common';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'app-picview-dark',
    templateUrl: './picview-dark.component.html',
    styleUrl: './picview-dark.component.scss'
})
export class PicviewDarkComponent implements OnInit, OnChanges {

    @Input() picData: any; // Can contain picFile, picTitle, fileType, documentExtn, contextId, documentId, documentNumber
    @Input() documentUrl: string = ''; // URL to copy for shared access
    @Output() closePicDialogue = new EventEmitter<any>();

    showPicDialog: boolean = true;
    pics: any[] = [];
    currentPicIndex: number = 0;
    fileCategory: string = 'image'; // 'pdf', 'video', 'audio', 'image', 'doc', 'cad', 'other'
    pdfUrl: SafeUrl | null = null;
    videoUrl: SafeUrl | null = null;
    audioUrl: SafeUrl | null = null;
    private dataProcessed: boolean = false;

    constructor(
        private sanitizer: DomSanitizer,
        private documentsService: DocumentsService,
        private commonService: CommonService,
        private toastr: ToastrService
    ) { }

    ngOnInit() {
        this.showPicDialog = true;
        if (this.picData && !this.dataProcessed) {
            this.processPicData();
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['picData'] && !this.dataProcessed) {
            this.processPicData();
        }
    }

    /**
     * Process the incoming picture data - handles both single and multiple images
     */
    private processPicData(): void {
        if (!this.picData) {
            return;
        }

        const fileExtn = this.picData?.documentExtn?.toLowerCase() || '';
        const base64 = this.picData?.picFile;

        // Get file category using centralized method
        this.fileCategory = this.commonService.getFileCategoryByExtension(fileExtn);

        // Handle file based on category
        if (this.fileCategory === 'pdf') {
            // Handle PDF
            if (base64?.startsWith('blob:')) {
                this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(base64);
            } else {
                const base64String = base64?.includes(',') ? base64.split(',')[1] : base64;
                const pdfUrl = 'data:application/pdf;base64,' + base64String;
                this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(pdfUrl);
            }
        } else if (this.fileCategory === 'video') {
            // Handle Video
            if (base64?.startsWith('blob:')) {
                this.videoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(base64);
            } else {
                this.videoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
                    this.documentsService.convertBase64ToFile(base64, 'video/mp4')
                );
            }
        } else if (this.fileCategory === 'audio') {
            // Handle Audio
            if (base64?.startsWith('blob:')) {
                this.audioUrl = this.sanitizer.bypassSecurityTrustResourceUrl(base64);
            } else {
                this.audioUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
                    this.documentsService.convertBase64ToFile(base64, 'audio/mp3')
                );
            }
        } else {
            // Handle images and other file types as images (default)
            if (Array.isArray(base64)) {
                this.pics = base64.map((pic: string) => this.formatImageData(pic));
            } else {
                // If only one image, make it an array for consistency
                this.pics = [this.formatImageData(base64)];
            }
        }
        this.dataProcessed = true;
    }

    /**
     * Format image data to ensure proper base64 data URI
     */
    private formatImageData(imageData: string): string {
        if (!imageData) {
            return '';
        }

        // If already has data URI prefix, return as is
        if (imageData.includes('data:image') || imageData.startsWith('blob:')) {
            return imageData;
        }

        // Add proper base64 data URI prefix
        return 'data:image/jpeg;base64,' + imageData;
    }

    /**
     * Navigate to next image
     */
    nextPic(): void {
        if (this.pics.length > 1) {
            this.currentPicIndex = (this.currentPicIndex + 1) % this.pics.length;
        }
    }

    /**
     * Navigate to previous image
     */
    prevPic(): void {
        if (this.pics.length > 1) {
            this.currentPicIndex = (this.currentPicIndex - 1 + this.pics.length) % this.pics.length;
        }
    }

    /**
     * Close the picture view dialog
     */
    closeDialogue(): void {
        this.showPicDialog = false;
        this.closePicDialogue.emit(false);
    }

    /**
     * Download the document using DocumentsService
     */
    downloadDocument(): void {
        if (!this.picData?.documentName || !this.picData?.picFile) {
            return;
        }

        // Log download access (fire-and-forget - don't wait for response)
        const documentId = this.picData?.documentId;
        const documentDumpId = this.picData?.documentDumpId;
        if (documentId || documentDumpId) {
            this.documentsService.logDocumentAccess(documentId || null, documentDumpId || null, 'download').subscribe({
                error: (err) => {
                    console.warn('Failed to log download access:', err);
                    // Continue with download even if logging fails
                }
            });
        }

        // Use DocumentsService method for downloading from base64
        if (this.picData.picFile && this.picData.picFile.startsWith('blob:')) {
            const a = document.createElement('a');
            a.href = this.picData.picFile;
            a.download = this.picData.documentName || 'downloaded-file';
            a.click();
        } else {
            this.documentsService.downloadFileFromBase64(this.picData.picFile, this.picData.documentName);
        }
    }

    /**
     * Copy document URL to clipboard
     */
    copyDocumentUrl(): void {

        let urlToCopy = this.documentUrl;

        // Generate URL if not provided - use picData properties
        if (!urlToCopy && this.picData?.contextId && (this.picData?.documentId || this.picData?.documentDumpId)) {
            const documentId = this.picData.documentId || this.picData.documentDumpId;
            const baseUrl = window.location.origin;
            const extn = this.picData.documentExtn ? `&documentExtn=${this.picData.documentExtn}` : '';
            // Only two types: 'dump' and 'report'. Default to 'report' if not dump.
            const docType = this.picData.docType === 'dump' ? 'dump' : 'report';
            const source = this.picData.source || 'project';
            urlToCopy = `${baseUrl}/${source}/${this.picData.contextId}/document/${documentId}?documentNumber=${this.picData.documentNumber || 0}&docType=${docType}&view=documentViewer${extn}`;
        }

        if (!urlToCopy) {
            this.toastr.error('Cannot generate document URL', 'Error');
            return;
        }

        this.copyToClipboard(urlToCopy);
        this.toastr.success('Document URL copied to clipboard!', 'Success', {
            timeOut: 3000
        });
    }

    /**
     * Helper method to copy text to clipboard
     */
    private copyToClipboard(text: string): void {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';

        document.body.appendChild(textarea);
        textarea.select();

        try {
            document.execCommand('copy');
        } catch (err) {
            console.error('Failed to copy to clipboard:', err);
        } finally {
            document.body.removeChild(textarea);
        }
    }

    /**
     * Get display counter for multiple images
     */
    getImageCounter(): string {
        return `${this.currentPicIndex + 1} / ${this.pics.length}`;
    }
}
